/**
 * Dollar figures in the person's own money, wherever a figure is only read:
 * the wallet's total, the market strip, the book's totals, the latest trades,
 * the ticket's total, and the money columns of Open orders and History. What
 * a person types or signs stays in USDC, with the local figure beside it.
 *
 * Only dollars are converted, never Gaju or XGE amounts, which keep their own
 * units and carry no money figure. The rates are the European Central
 * Bank's daily reference rates, read by the browser from frankfurter (free,
 * keyless) at most once a day and kept in localStorage; the box never makes
 * the call, so its traffic stays flat. When the day's rates lack the chosen
 * currency the line says so; when there are no rates at all it is not shown.
 */

export const RATES_URL = 'https://api.frankfurter.dev/v1/latest?from=USD';
const RATES_KEY = 'xc.usdRates';
const CHOICE_KEY = 'xc.localCurrency';
const DAY = 24 * 60 * 60 * 1000;
/** A rate a few days old still reads fine as "about"; older than this, nothing. */
const STALE = 7 * DAY;

/** The currencies the ECB publishes against the dollar. */
export const CURRENCIES = [
  'AUD',
  'BRL',
  'CAD',
  'CHF',
  'CNY',
  'CZK',
  'DKK',
  'EUR',
  'GBP',
  'HKD',
  'HUF',
  'IDR',
  'ILS',
  'INR',
  'ISK',
  'JPY',
  'KRW',
  'MXN',
  'MYR',
  'NOK',
  'NZD',
  'PHP',
  'PLN',
  'RON',
  'SEK',
  'SGD',
  'THB',
  'TRY',
  'ZAR',
] as const;

const EURO = 'AT BE CY DE EE ES FI FR GR HR IE IT LT LU LV MT NL PT SI SK';
const REGION: Record<string, string> = {
  AU: 'AUD',
  BR: 'BRL',
  CA: 'CAD',
  CH: 'CHF',
  LI: 'CHF',
  CN: 'CNY',
  CZ: 'CZK',
  DK: 'DKK',
  GB: 'GBP',
  HK: 'HKD',
  HU: 'HUF',
  ID: 'IDR',
  IL: 'ILS',
  IN: 'INR',
  IS: 'ISK',
  JP: 'JPY',
  KR: 'KRW',
  MX: 'MXN',
  MY: 'MYR',
  NO: 'NOK',
  NZ: 'NZD',
  PH: 'PHP',
  PL: 'PLN',
  RO: 'RON',
  SE: 'SEK',
  SG: 'SGD',
  TH: 'THB',
  TR: 'TRY',
  ZA: 'ZAR',
  NA: 'ZAR',
  LS: 'ZAR',
};
for (const r of EURO.split(' ')) REGION[r] = 'EUR';

/** The currency of the browser's region, or null (US, or a region the ECB does not cover). */
export function currencyForLocale(locale: string | undefined): string | null {
  if (locale === undefined || locale === '') return null;
  try {
    const region = new Intl.Locale(locale).maximize().region;
    return region === undefined ? null : (REGION[region] ?? null);
  } catch {
    return null;
  }
}

function store(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * The browser's languages in order of preference. The first one that names a
 * region decides (so en-US then en-ZA stays the US); with none naming a
 * region, the first language's likely region does.
 */
export function regionalLocale(languages: readonly string[]): string | undefined {
  for (const l of languages) {
    try {
      if (new Intl.Locale(l).region !== undefined) return l;
    } catch {
      // not a locale; try the next
    }
  }
  return languages[0];
}

function browserLocale(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const list = navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  return regionalLocale(list);
}

/** The currency chosen in Account, or the browser's; null means no line. */
export function localCurrency(locale: string | undefined = browserLocale()): string | null {
  const chosen = store()?.getItem(CHOICE_KEY) ?? null;
  if (chosen === 'off') return null;
  if (chosen !== null && (CURRENCIES as readonly string[]).includes(chosen)) return chosen;
  return currencyForLocale(locale);
}

export function setLocalCurrency(code: string): void {
  store()?.setItem(CHOICE_KEY, code);
}

interface Cached {
  at: number;
  rates: Record<string, number>;
}

function cached(): Cached | null {
  try {
    const raw = store()?.getItem(RATES_KEY) ?? null;
    if (raw === null) return null;
    const c = JSON.parse(raw) as { at?: unknown; rates?: unknown };
    return typeof c.at === 'number' && typeof c.rates === 'object' && c.rates !== null
      ? (c as Cached)
      : null;
  } catch {
    return null;
  }
}

/**
 * Dollar rates, from the cache when under a day old, otherwise fetched once.
 * A cache from an earlier visit that lacks the wanted currency is fetched
 * again, once per currency per page load. A failed fetch falls back to a
 * cache under a week old, then to null.
 */
let inflight: Promise<Record<string, number> | null> | null = null;
const retried = new Set<string>();
/** True once this page load has fetched the rates itself: a second fetch would say the same. */
let fetchedHere = false;

export async function usdRates(
  now: number = Date.now(),
  fetcher: typeof fetch = fetch,
  want?: string,
): Promise<Record<string, number> | null> {
  const c = cached();
  if (c !== null && now - c.at < DAY) {
    if (want === undefined || want in c.rates || fetchedHere || retried.has(want)) return c.rates;
    retried.add(want);
  }
  // One request at a time, however many screens ask at once.
  inflight ??= fetchRates(now, fetcher, c).finally(() => {
    inflight = null;
  });
  return inflight;
}

/** For tests: forget what this page load has fetched. */
export function forgetFetches(): void {
  retried.clear();
  fetchedHere = false;
}

async function fetchRates(
  now: number,
  fetcher: typeof fetch,
  c: Cached | null,
): Promise<Record<string, number> | null> {
  try {
    const res = await fetcher(RATES_URL);
    if (!res.ok) throw new Error(String(res.status));
    const body = (await res.json()) as { rates?: Record<string, unknown> };
    const rates: Record<string, number> = {};
    for (const [k, v] of Object.entries(body.rates ?? {}))
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) rates[k] = v;
    if (Object.keys(rates).length === 0) throw new Error('no rates');
    store()?.setItem(RATES_KEY, JSON.stringify({ at: now, rates }));
    fetchedHere = true;
    return rates;
  } catch {
    return c !== null && now - c.at < STALE ? c.rates : null;
  }
}

/**
 * A dollar figure in the chosen currency, for something a person only reads.
 * From ten up it loses the cents ("R 1,850"), between one and ten it keeps
 * them ("£3.75"), and under one it keeps three figures, so a price of a
 * fraction of a cent still reads ("R0.074"). `compact` shortens the big ones
 * for the market cap ("R7.4M"). Null when there is no currency, no rates or
 * no rate for the currency today, and for a figure that is not money at all:
 * the caller then shows the dollars, or the asset's own units, alone.
 */
export function localOf(
  usd: number | null,
  currency: string | null,
  rates: Record<string, number> | null,
  opts: { locale?: string | undefined; compact?: boolean } = {},
): string | null {
  if (usd === null || currency === null || rates === null) return null;
  if (!Number.isFinite(usd) || usd <= 0) return null;
  const rate = rates[currency];
  if (rate === undefined) return null;
  const value = usd * rate;
  const format: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  };
  if (opts.compact === true) {
    format.notation = 'compact';
    format.maximumFractionDigits = 1;
  } else if (value >= 10) {
    format.maximumFractionDigits = 0;
    format.minimumFractionDigits = 0;
  } else if (value >= 1) {
    format.maximumFractionDigits = 2;
    format.minimumFractionDigits = 2;
  } else {
    format.maximumSignificantDigits = 3;
  }
  try {
    return new Intl.NumberFormat(opts.locale, format).format(value);
  } catch {
    return null;
  }
}

/**
 * "about R 74" for a dollar figure, to go under a box whose figure stays in
 * dollars because it is typed or signed. Null when there is no currency, no
 * rate for it today, or nothing to convert.
 */
export function aboutOf(
  usd: number | null,
  currency: string | null,
  rates: Record<string, number> | null,
  locale?: string,
): string | null {
  const text = localOf(usd, currency, rates, { locale });
  return text === null ? null : `about ${text}`;
}

/**
 * The same, for a USDC amount in minor units (six decimals), taking one USDC
 * as one dollar. Null when there is no amount, as well.
 */
export function localAmount(
  usdcMinor: bigint,
  currency: string | null,
  rates: Record<string, number> | null,
  locale?: string,
): string | null {
  if (usdcMinor <= 0n) return null;
  return localOf(Number(usdcMinor) / 1e6, currency, rates, { locale });
}

/**
 * "about R 1,850" for a USDC amount; "no rate for JPY today" when the day's
 * rates lack the currency; null when there are no rates, no currency or no
 * balance.
 */
export function aboutLocal(
  usdcMinor: bigint,
  currency: string | null,
  rates: Record<string, number> | null,
  locale?: string,
): string | null {
  if (currency === null || rates === null || usdcMinor <= 0n) return null;
  if (rates[currency] === undefined) return `no rate for ${currency} today`;
  const text = localAmount(usdcMinor, currency, rates, locale);
  return text === null ? null : `about ${text}`;
}

/** A currency's name in the person's language, for the Account picker. */
export function currencyName(code: string, locale?: string): string {
  try {
    return new Intl.DisplayNames(locale, { type: 'currency' }).of(code) ?? code;
  } catch {
    return code;
  }
}
